import { Close } from '@mui/icons-material';
import { IconButton, Stack, Typography } from '@mui/material';
import { StakeErrorType, StyleObj } from '../../@types';
import { removeBetslipBet } from '../../stores/betslip/selectors';
import type { BetslipEvent } from '../../types/betslip';
import { parseNumberWithDecimals, prepareStakeMessages } from '../../helpers';
import { useBetBuilderItem } from '../../hooks/useBetBuilderItem';
import useLocalization from '../../hooks/useLocalization';
import { useGlobalTicketConditions } from '../../queries';
import { WalletType } from '../../types/enums';
import { CurrencySymbol } from '../atoms/CurrencySymbol';
import BetBuilderLegList from '../molecules/BetBuilderLegList';
import NumberInput from '../atoms/NumberInput';
import OddsDisplay from '../atoms/OddsDisplay';

import { mergeSx } from '../../utils/styles';

const styles: StyleObj = {
  container: {
    position: 'relative',
    backgroundColor: 'neutral.100',
    '&:not(:first-of-type)': {
      borderTop: '1px solid',
      borderColor: 'neutral.200',
    },
    '&:not(:last-of-type)': {
      borderBottom: '1px solid',
      borderColor: 'neutral.200',
    },
  },
  content: {
    flex: 1,
    minWidth: 0,
    py: 1,
    px: 1.5,
  },
  header: {
    minWidth: 0,
  },
  removeButton: {
    p: 0.5,
    color: 'primary.main',
  },
  removeIcon: {
    fontSize: 14,
  },
  stakeColumn: {
    width: 90,
    borderLeft: '1px solid',
    borderColor: 'neutral.200',
    backgroundColor: 'neutral.100',
  },
  stakeInput: {
    width: 90,
    height: 40,
    flexDirection: 'row',
    '& fieldset': { border: 'none' },
    '& .MuiInputBase-input': {
      color: 'primary.main',
      textAlign: 'right',
      fontWeight: 600,
      fontSize: 14,
    },
    background: 'white',
  },
};

type BetBuilderBetslipItemProps = {
  bet: BetslipEvent;
  value: string;
  onChange: (value: string) => void;
  onRemoveBet: (outcomeId: string) => void;
  errorMessages?: { errorType: StakeErrorType };
  selectedWallet: WalletType;
};

const BetBuilderBetslipItem = ({
  bet,
  value,
  onChange,
  onRemoveBet,
  errorMessages,
  selectedWallet,
}: BetBuilderBetslipItemProps) => {
  const { t } = useLocalization();
  const { legs } = useBetBuilderItem({ type: 'betslip', bet });
  const { data: globalTicketConditions } = useGlobalTicketConditions();

  const betslipTicketType = bet.isLive ? 'inPlay' : 'preMatch';
  const stakeMessage = prepareStakeMessages(
    betslipTicketType,
    selectedWallet,
    globalTicketConditions,
    errorMessages?.errorType
  );
  const { type: stakeErrorType, amount: stakeErrorAmount } = stakeMessage ?? {};
  const possibleReturn = Number(value || 0) * Number(bet.odds || 0);

  if (!bet.betBuilder) {
    return null;
  }

  const handleRemoveLeg = (outcomeId: string) => {
    removeBetslipBet(outcomeId);
    onRemoveBet(outcomeId);
  };

  return (
    <Stack direction='row' sx={styles.container} data-testid={`bet-builder-item-${bet.eventId}`}>
      <Stack sx={styles.content} spacing={1}>
        <Stack
          direction='row'
          spacing={1}
          sx={mergeSx(
            {
              justifyContent: 'space-between',
            },
            styles.header
          )}
        >
          <Stack
            spacing={0.5}
            sx={{
              minWidth: 0,
              textAlign: 'left',
            }}
          >
            <Typography
              variant='subtitle2'
              sx={{
                color: 'neutral.600',
              }}
            >
              {bet.eventName}
            </Typography>
            <Typography
              variant='body3'
              sx={{
                color: 'neutral.600',
              }}
            >
              {t('betBuilder')}
            </Typography>
          </Stack>
          <OddsDisplay
            value={bet.odds}
            variant='subtitle2'
            sx={{
              color: 'neutral.600',
              py: 0.5,
              px: 0.5,
              height: 'fit-content',
            }}
          />
        </Stack>

        <BetBuilderLegList
          legs={legs}
          renderAction={(leg) => (
            <IconButton
              key={leg.outcomeId}
              size='small'
              sx={styles.removeButton}
              onClick={() => handleRemoveLeg(leg.outcomeId)}
              aria-label={t('remove')}
              data-testid={`remove-bet-builder-leg-${leg.outcomeId}`}
            >
              <Close sx={styles.removeIcon} />
            </IconButton>
          )}
        />

        {stakeErrorType && (
          <Typography
            variant='body3'
            sx={{
              fontSize: 9,
              textAlign: 'center',
              color: 'error.600',
            }}
          >
            {t(stakeErrorType)} <CurrencySymbol />
            {stakeErrorAmount && parseNumberWithDecimals(stakeErrorAmount, 2)}
          </Typography>
        )}
      </Stack>

      <Stack
        spacing={0.5}
        sx={mergeSx(
          {
            alignItems: 'end',
          },
          styles.stakeColumn
        )}
      >
        <NumberInput
          size='small'
          variant='standard'
          placeholder={t('stake')}
          sx={styles.stakeInput}
          onChange={onChange}
          value={value}
          disabled={bet.disabled}
        />
        <Typography
          variant='body3'
          sx={{
            pr: 0.5,
            fontSize: 9,
            color: 'neutral.600',
          }}
        >
          {t('toReturn')}: <CurrencySymbol />
          {parseNumberWithDecimals(possibleReturn || 0, 2)}
        </Typography>
      </Stack>
    </Stack>
  );
};

export default BetBuilderBetslipItem;
